import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Wallet, Plus, TrendingUp, TrendingDown, Loader2, Mail, MessageSquare,
  Image as ImageIcon, Calendar, Ticket, Sparkles, ExternalLink, ArrowRight,
  CreditCard, History, ShieldCheck, Building2, Search, MoreHorizontal,
  Edit2, Eye, Download, FileText, CalendarDays, Wrench,
} from 'lucide-react';
import VenueInvitesPanel from '../components/VenueInvitesPanel';
import AutoTopupCard from '../components/AutoTopupCard';
import EmbedCodeDialogButton from '../components/EmbedCodeDialogButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });

const TOPUP_PRESETS = [2000, 5000, 10000, 20000, 50000];   // $20, $50, $100, $200, $500
const fmtUSD = (cents) => `$${(((cents || 0) / 100)).toFixed(2)}`;

/**
 * /organization/dashboard — the home screen for an Organization user.
 * Shows wallet balance + top-up + transaction ledger + quick stats. Built
 * to match the rest of the platform's dark-purple aesthetic.
 */
export default function OrganizationDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [eventFilter, setEventFilter] = useState('upcoming'); // upcoming | drafts | past
  const [eventQuery, setEventQuery] = useState('');
  // Kind filter: 'all' | 'event' | 'experience' | 'reservation'. Lets
  // admins scope down to just tours OR just table reservations OR just
  // ticketed events when the catalog mixes all three.
  const [kindFilter, setKindFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const isAdmin = user?.role === 'admin';
      // Admins don't have a personal org — load only the platform-wide
      // event list so the page can act as a unified Event Manager for both
      // roles. Org users still see their full wallet + dashboard panels.
      const orgCalls = isAdmin
        ? [Promise.resolve({ data: null }), Promise.resolve({ data: null }),
           Promise.resolve({ data: null }), Promise.resolve({ data: { transactions: [] } })]
        : [
          axios.get(`${API}/organizations/me/dashboard`, auth()),
          axios.get(`${API}/organizations/me/wallet`, auth()),
          axios.get(`${API}/organizations/me/wallet/pricing`),
          axios.get(`${API}/organizations/me/wallet/transactions?limit=25`, auth()),
        ];
      const [d, w, p, tx, ev] = await Promise.all([
        ...orgCalls,
        axios.get(`${API}/hosted-events?include_drafts=true&limit=200`, auth()),
      ]);
      setDashboard(d.data);
      setWallet(w.data);
      setPricing(p.data);
      setTransactions(tx.data?.transactions || []);
      setEvents(ev.data.events || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Sign in to access the Event Manager');
        navigate('/login');
      } else if (status === 404 && user?.role !== 'admin') {
        toast.error('No organization profile found — apply first');
        navigate('/organizations/apply');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, user?.role]);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe success / cancel redirect query params
  useEffect(() => {
    const success = params.get('topup_success');
    const sid = params.get('session_id');
    const cancelled = params.get('topup_cancelled');
    if (success && sid) {
      (async () => {
        try {
          const res = await axios.get(
            `${API}/organizations/me/wallet/topup/confirm?session_id=${encodeURIComponent(sid)}`,
            auth(),
          );
          if (res.data?.credited) {
            toast.success(`Wallet credited ${fmtUSD(res.data.amount_cents)}`);
          } else if (res.data?.already_credited) {
            toast.info('Top-up already credited');
          } else {
            toast.info('Payment is still processing — refresh in a moment');
          }
        } catch (e) {
          toast.error('Could not confirm top-up — please refresh');
        }
        setParams({}, { replace: true });
        load();
      })();
    } else if (cancelled) {
      toast.info('Top-up cancelled');
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Bucket events into upcoming / drafts / past for the filter pills.
  // (Hooks must run unconditionally — keep these BEFORE any early return.)
  const today = new Date().toISOString().slice(0, 10);
  const buckets = useMemo(() => {
    const ups = [], drafts = [], pasts = [];
    for (const e of events) {
      if (!e.is_published) drafts.push(e);
      else if ((e.event_date || '') >= today) ups.push(e);
      else pasts.push(e);
    }
    ups.sort((a, b) => (a.event_date || '').localeCompare(b.event_date || ''));
    pasts.sort((a, b) => (b.event_date || '').localeCompare(a.event_date || ''));
    drafts.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    return { upcoming: ups, drafts, past: pasts };
  }, [events, today]);

  const visibleEvents = useMemo(() => {
    let list = buckets[eventFilter] || [];
    if (kindFilter !== 'all') {
      list = list.filter((e) => {
        const k = e.kind || 'event';
        return k === kindFilter;
      });
    }
    const q = eventQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) =>
      [e.name, e.tagline, e.venue, e.city, e.category]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [buckets, eventFilter, eventQuery, kindFilter]);

  // Per-kind counts within the active status bucket — used to render
  // the "All / Events / Experiences / Reservations" chip badges.
  const kindCountsInBucket = useMemo(() => {
    const list = buckets[eventFilter] || [];
    const counts = { all: list.length, event: 0, experience: 0, reservation: 0 };
    for (const e of list) {
      const k = e.kind || 'event';
      if (counts[k] !== undefined) counts[k] += 1;
    }
    return counts;
  }, [buckets, eventFilter]);

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  const org = dashboard?.organization || {};
  const stats = dashboard?.stats || {};
  const balance = wallet?.balance_cents ?? 0;
  const isLow = balance < 500;     // < $5
  const isCritical = balance < 100; // < $1

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="organization-dashboard">
      <div className="max-w-7xl mx-auto px-4 space-y-6">

        {/* Header — org identity left, compact wallet pill + Create Event right */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-400">
              Event Manager
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] mt-1 flex items-center flex-wrap gap-2">
              {user?.role === 'admin'
                ? 'Eventism · All events'
                : (org.display_name || org.legal_name || 'My Organization')}
              {org.is_verified && (
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 align-middle">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Verified
                </Badge>
              )}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {user?.role === 'admin' ? (
                <span>Platform-wide event manager. Same UI organizers use.</span>
              ) : (
                org.slug && (
                  <a
                    href={`/organizations/${org.slug}`}
                    className="underline decoration-dotted hover:text-indigo-400"
                    data-testid="org-public-profile-link"
                  >
                    Public profile <ExternalLink className="h-3 w-3 inline ml-0.5" />
                  </a>
                )
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Compact wallet pill — orgs only */}
            {user?.role !== 'admin' && (
              <button
                onClick={() => setShowTopup(true)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                  isCritical
                    ? 'border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15'
                    : isLow
                      ? 'border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15'
                      : 'border-border bg-card/60 hover:border-indigo-500/40'
                }`}
                title="Click to top up"
                data-testid="org-wallet-pill"
              >
                <Wallet className={`h-4 w-4 ${isCritical ? 'text-rose-300' : isLow ? 'text-amber-300' : 'text-indigo-300'}`} />
                <span className="font-semibold tabular-nums" data-testid="org-wallet-balance">{fmtUSD(balance)}</span>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">Top up</span>
              </button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/admin/brands')}
              data-testid="org-brands-btn"
            >
              Series &amp; Clubs
            </Button>
            <Button
              onClick={() => navigate('/admin/events/legacy?new=1')}
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white"
              data-testid="org-create-event-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Create event
            </Button>
          </div>
        </div>

        {/* Tabs — Events / Wallet / Venues. Events is the primary focus now. */}
        <Tabs defaultValue="events" className="space-y-5" data-testid="org-dashboard-tabs">
          <TabsList className="bg-card border border-border" data-testid="org-tabs-list">
            <TabsTrigger value="events" className="data-[state=active]:bg-indigo-500/15 data-[state=active]:text-indigo-100" data-testid="tab-events">
              <Calendar className="h-4 w-4 mr-1.5" /> Events
            </TabsTrigger>
            {user?.role !== 'admin' && (
              <>
                <TabsTrigger value="wallet" className="data-[state=active]:bg-indigo-500/15 data-[state=active]:text-indigo-100" data-testid="tab-wallet">
                  <Wallet className="h-4 w-4 mr-1.5" /> Wallet
                </TabsTrigger>
                <TabsTrigger value="venues" className="data-[state=active]:bg-indigo-500/15 data-[state=active]:text-indigo-100" data-testid="tab-venues">
                  <Building2 className="h-4 w-4 mr-1.5" /> Venues
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* ============ EVENTS TAB ============ */}
          <TabsContent value="events" className="space-y-4">
            {/* Stat tiles — compact */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="org-event-stats">
              <StatTile icon={Calendar} label="Total events"   value={events.length} />
              <StatTile icon={Sparkles} label="Published"      value={buckets.upcoming.length + buckets.past.length} />
              <StatTile icon={FileText} label="Drafts"         value={buckets.drafts.length} />
              <StatTile icon={Ticket}   label="Tickets sold"   value={stats.tickets_sold ?? 0} />
            </div>

            {/* Search + filter pills */}
            <div className="flex flex-col md:flex-row md:items-center gap-3" data-testid="events-toolbar">
              <div className="relative flex-1 max-w-md">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={eventQuery}
                  onChange={(e) => setEventQuery(e.target.value)}
                  placeholder="Search events…"
                  className="pl-9 h-10"
                  data-testid="events-search"
                />
              </div>
              <div className="flex items-center gap-1" data-testid="events-filter-pills">
                {[
                  { v: 'upcoming', label: 'Upcoming', n: buckets.upcoming.length },
                  { v: 'drafts',   label: 'Drafts',   n: buckets.drafts.length },
                  { v: 'past',     label: 'Past',     n: buckets.past.length },
                ].map((f) => (
                  <button
                    key={f.v}
                    onClick={() => setEventFilter(f.v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      eventFilter === f.v
                        ? 'border-indigo-400 bg-indigo-500/15 text-indigo-100'
                        : 'border-border hover:border-indigo-500/40 text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`events-pill-${f.v}`}
                  >
                    {f.label} <span className="opacity-60">({f.n})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Kind filter strip — only renders when the org actually has
                experiences / reservations, so legacy ticketed orgs see
                zero extra UI noise. */}
            {(kindCountsInBucket.experience > 0 || kindCountsInBucket.reservation > 0) && (
              <div className="flex items-center gap-1 flex-wrap" data-testid="events-kind-filter">
                {[
                  { v: 'all',         label: 'All types',    cls: 'border-foreground/30' },
                  { v: 'event',       label: 'Events',       cls: 'border-indigo-500/40 text-indigo-300' },
                  { v: 'experience',  label: 'Experiences',  cls: 'border-emerald-500/40 text-emerald-300' },
                  { v: 'reservation', label: 'Reservations', cls: 'border-amber-500/40 text-amber-300' },
                ].map((k) => (
                  <button
                    key={k.v}
                    onClick={() => setKindFilter(k.v)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      kindFilter === k.v ? `${k.cls} bg-card/50` : 'border-border text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`events-kind-pill-${k.v}`}
                  >
                    {k.label} <span className="opacity-60">({kindCountsInBucket[k.v]})</span>
                  </button>
                ))}
              </div>
            )}

            {/* Event list table */}
            <Card data-testid="org-events-list">
              <CardContent className="p-0">
                {visibleEvents.length === 0 ? (
                  <div className="py-16 px-6 text-center space-y-3" data-testid="events-empty">
                    <Calendar className="h-10 w-10 mx-auto text-muted-foreground" />
                    <p className="font-bold text-lg">
                      {eventFilter === 'upcoming' && 'No upcoming events yet'}
                      {eventFilter === 'drafts'   && 'No drafts yet'}
                      {eventFilter === 'past'     && 'No past events yet'}
                    </p>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      {eventFilter === 'drafts'
                        ? 'Drafts are events you haven\'t published yet — start one and finish whenever.'
                        : 'Your published events will appear here.'}
                    </p>
                    <Button
                      onClick={() => navigate('/admin/events/legacy?new=1&as=organizer')}
                      className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white"
                      data-testid="events-empty-create-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Create your first event
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-[1fr,80px,90px,90px,40px] gap-3 px-5 py-3 border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground font-bold">
                      <div>Event</div>
                      <div className="text-right">Sold</div>
                      <div className="text-right">Gross</div>
                      <div>Status</div>
                      <div></div>
                    </div>
                    <div className="divide-y divide-border">
                      {visibleEvents.map((e) => (
                        <EventRow key={e.event_id} event={e} navigate={navigate} />
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* CSV export hint */}
            {visibleEvents.length > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground px-1" data-testid="events-footer">
                <span>{visibleEvents.length} {eventFilter} event{visibleEvents.length === 1 ? '' : 's'}</span>
                <button
                  onClick={() => exportEventsCsv(visibleEvents, eventFilter)}
                  className="flex items-center gap-1 hover:text-indigo-400 transition-colors"
                  data-testid="events-csv-export"
                >
                  <Download className="h-3 w-3" /> CSV Export
                </button>
              </div>
            )}
          </TabsContent>

          {/* ============ WALLET TAB ============ */}
          <TabsContent value="wallet" className="space-y-5">
            {/* Wallet detail card (full size on the wallet tab) */}
            <Card
              className={`relative overflow-hidden border-2 ${isCritical ? 'border-rose-500/40' : isLow ? 'border-amber-500/40' : 'border-indigo-500/30'}`}
              data-testid="org-wallet-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-fuchsia-500/5 to-transparent pointer-events-none" />
              <CardContent className="p-6 sm:p-8 relative">
                <div className="grid lg:grid-cols-[1fr,auto] gap-6 items-start">
                  <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                      <Wallet className="h-4 w-4" /> Wallet balance
                    </div>
                    <p className="text-5xl sm:text-6xl font-bold font-['Space_Grotesk'] mt-2 tabular-nums">
                      {fmtUSD(balance)}
                    </p>
                    {isCritical && (
                      <p className="text-rose-300 text-sm mt-2" data-testid="wallet-low-warning">
                        Balance is critically low — top up to keep campaigns running.
                      </p>
                    )}
                    {isLow && !isCritical && (
                      <p className="text-amber-300 text-sm mt-2" data-testid="wallet-low-warning">
                        Balance is running low — consider topping up.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-5">
                      <Button
                        onClick={() => setShowTopup(true)}
                        className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-bold h-11 px-5"
                        data-testid="org-topup-open-btn"
                      >
                        <CreditCard className="h-4 w-4 mr-2" /> Top up wallet
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/organization/marketing')}
                        className="h-11"
                        data-testid="org-go-marketing-btn"
                      >
                        <Sparkles className="h-4 w-4 mr-2" /> Run a campaign
                      </Button>
                    </div>
                  </div>
                  <div className="bg-card/60 border border-border rounded-lg p-4 min-w-[240px]" data-testid="org-pricing-card">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-3">
                      Per-message pricing
                    </p>
                    <PricingRow icon={Mail} label="Email" cents={pricing?.units?.email?.price_cents} hint="10 emails = 1¢" />
                    <PricingRow icon={MessageSquare} label="SMS" cents={pricing?.units?.sms?.price_cents} hint="$0.015 / send" />
                    <PricingRow icon={ImageIcon} label="MMS" cents={pricing?.units?.mms?.price_cents} hint="$0.04 / send" />
                    <p className="text-[10px] text-muted-foreground mt-3">
                      Charged from wallet on send. No subscription.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <AutoTopupCard onChange={load} />

            {/* Ledger */}
            <Card data-testid="org-wallet-ledger-card">
              <CardContent className="p-0">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-indigo-300" />
                    <h2 className="font-bold">Wallet activity</h2>
                  </div>
                  <p className="text-xs text-muted-foreground">Last 25 entries</p>
                </div>
                {transactions.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground" data-testid="ledger-empty">
                    No transactions yet — top up to start running campaigns.
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {transactions.map((tx) => (
                      <LedgerRow key={tx.txn_id} tx={tx} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ VENUES TAB ============ */}
          <TabsContent value="venues" className="space-y-6">
            <VenueInvitesPanel />
          </TabsContent>
        </Tabs>
      </div>

      <TopupDialog
        open={showTopup}
        onClose={() => setShowTopup(false)}
        pricing={pricing}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event row — Eventbrite-style list item with thumb, name, sold, gross, status
// ---------------------------------------------------------------------------
const STATUS_STYLE = {
  upcoming: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  draft:    'bg-amber-500/15 text-amber-300 border-amber-500/30',
  past:     'bg-muted text-muted-foreground border-border',
  sold_out: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

function EventRow({ event, navigate }) {
  const today = new Date().toISOString().slice(0, 10);
  const isDraft = !event.is_published;
  const isPast = !isDraft && (event.event_date || '') < today;
  const statusKey = isDraft ? 'draft' : isPast ? 'past' : 'upcoming';
  const statusLabel = isDraft ? 'Draft' : isPast ? 'Past' : 'On Sale';

  const monthDay = (() => {
    if (!event.event_date) return { mo: '—', day: '—' };
    try {
      const d = new Date(`${event.event_date}T00:00:00`);
      return {
        mo: d.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
        day: d.getDate(),
      };
    } catch { return { mo: '?', day: '?' }; }
  })();

  const sold = event.tickets_sold || 0;
  const cap = event.ticket_capacity || event.max_capacity || 0;
  const gross = event.gross_revenue_cents != null
    ? `$${(event.gross_revenue_cents / 100).toFixed(2)}`
    : `$${(sold * (event.ticket_price_min || 0)).toFixed(2)}`;

  const editHref = `/admin/events/legacy?edit=${event.event_id}&from=dashboard`;
  const publicPrefix = event.kind === 'experience' ? '/ex' : (event.kind === 'reservation' ? '/res' : '/events');
  const publicHref = `${publicPrefix}/${event.city || 'denver'}/${event.slug || event.event_id}`;
  const builderHref = `/admin/events/${event.event_id}/page-builder`;
  const manageHref = `/event/${event.event_id}/manage`;
  // Kind badge — quick visual distinguisher in the events list.
  const kindMeta = event.kind === 'experience'
    ? { label: 'Experience', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' }
    : event.kind === 'reservation'
      ? { label: 'Reservation', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' }
      : { label: 'Event', cls: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' };

  return (
    <div
      className="grid grid-cols-[1fr,80px,90px,90px,40px] gap-3 px-5 py-3 items-center hover:bg-card/40 transition-colors"
      data-testid={`event-row-${event.event_id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* Date block */}
        <div className="shrink-0 h-12 w-12 rounded-lg bg-card border border-border flex flex-col items-center justify-center">
          <span className="text-[9px] font-bold text-rose-300 uppercase">{monthDay.mo}</span>
          <span className="text-base font-bold tabular-nums leading-none">{monthDay.day}</span>
        </div>
        {/* Thumb */}
        {event.image_url ? (
          <img src={event.image_url} alt="" className="shrink-0 h-12 w-16 rounded object-cover" />
        ) : (
          <div className="shrink-0 h-12 w-16 rounded bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20 flex items-center justify-center">
            <CalendarDays className="h-5 w-5 text-indigo-300/70" />
          </div>
        )}
        <div className="min-w-0">
          <Link
            to={manageHref}
            className="font-bold text-sm hover:text-indigo-300 transition-colors truncate block"
            data-testid={`event-row-name-${event.event_id}`}
          >
            {event.name}
          </Link>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider border ${kindMeta.cls}`}
              data-testid={`event-row-kind-${event.event_id}`}
            >
              {kindMeta.label}
            </span>
            <p className="text-xs text-muted-foreground truncate">
              {event.event_date && new Date(`${event.event_date}T00:00:00`).toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
              })}
              {event.venue && ` · ${event.venue}`}
            </p>
          </div>
        </div>
      </div>
      <div className="text-right text-sm tabular-nums">
        {sold}{cap ? ` / ${cap}` : ''}
      </div>
      <div className="text-right text-sm font-semibold tabular-nums">{gross}</div>
      <div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_STYLE[statusKey]}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${
            statusKey === 'upcoming' ? 'bg-emerald-400' : statusKey === 'draft' ? 'bg-amber-400' : 'bg-muted-foreground'
          }`} />
          {statusLabel}
        </span>
      </div>
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={() => navigate(manageHref)}
          className="p-1.5 rounded hover:bg-card border border-transparent hover:border-border text-indigo-300 hover:text-indigo-200 transition-colors"
          title="Manage event"
          data-testid={`event-row-manage-${event.event_id}`}
        >
          <Wrench className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => navigate(editHref)}
          className="p-1.5 rounded hover:bg-card border border-transparent hover:border-border text-muted-foreground hover:text-foreground transition-colors"
          title="Edit details"
          data-testid={`event-row-edit-${event.event_id}`}
        >
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        {/* Embed code — only renders for experiences & reservations.
            Component returns null for kind=event so the icon strip stays
            tight for classic ticketed events. */}
        <EmbedCodeDialogButton
          event={event}
          testid={`event-row-embed-${event.event_id}`}
        />
        {!isDraft && (
          <a
            href={publicHref}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded hover:bg-card border border-transparent hover:border-border text-muted-foreground hover:text-foreground transition-colors"
            title="View public page"
            data-testid={`event-row-view-${event.event_id}`}
          >
            <Eye className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

// CSV export — client-side; works for any bucket size we render.
function exportEventsCsv(events, bucket) {
  const rows = [['Name', 'Date', 'Venue', 'City', 'Status', 'Sold', 'Capacity', 'Gross']];
  for (const e of events) {
    rows.push([
      e.name || '',
      e.event_date || '',
      e.venue || '',
      e.city || '',
      e.is_published ? 'Published' : 'Draft',
      e.tickets_sold || 0,
      e.ticket_capacity || e.max_capacity || '',
      e.gross_revenue_cents != null ? (e.gross_revenue_cents / 100).toFixed(2) : '',
    ]);
  }
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `events-${bucket}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}


// ---------------------------------------------------------------------------

function PricingRow({ icon: Icon, label, cents, hint }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <span className="font-semibold tabular-nums text-indigo-200" title={hint}>
        {cents !== undefined ? (cents < 1 ? `${(cents * 10).toFixed(1)}¢ / 10` : `${cents.toFixed(1)}¢`) : '—'}
      </span>
    </div>
  );
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <Card className="border-border" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <p className="text-3xl font-bold mt-1 tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function LedgerRow({ tx }) {
  const isCredit = tx.amount_cents > 0;
  const Icon = isCredit ? TrendingUp : TrendingDown;
  const colour = isCredit ? 'text-emerald-300' : 'text-rose-300';
  const KIND_LABELS = {
    topup: 'Wallet top-up',
    refund: 'Refund',
    adjustment: 'Admin adjustment',
    usage_email: 'Email send',
    usage_sms: 'SMS send',
    usage_mms: 'MMS send',
  };
  const label = KIND_LABELS[tx.kind] || tx.kind;
  const subtitle =
    tx.units != null && tx.unit_kind
      ? `${tx.units.toLocaleString()} × ${tx.unit_kind}`
      : tx.ref?.reason
        ? tx.ref.reason
        : '';
  return (
    <div className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`ledger-row-${tx.txn_id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`h-9 w-9 rounded-full bg-card border border-border flex items-center justify-center ${colour}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{label}</p>
          {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
        </div>
      </div>
      <div className="text-right">
        <p className={`font-semibold tabular-nums ${colour}`}>
          {isCredit ? '+' : ''}{fmtUSD(tx.amount_cents)}
        </p>
        <p className="text-[11px] text-muted-foreground tabular-nums">
          Bal: {fmtUSD(tx.balance_after_cents)}
        </p>
      </div>
    </div>
  );
}

function TopupDialog({ open, onClose, pricing }) {
  const [selected, setSelected] = useState(TOPUP_PRESETS[1]);
  const [submitting, setSubmitting] = useState(false);
  const min = pricing?.topup?.min_cents || 2000;
  const max = pricing?.topup?.max_cents || 50000;

  const onSubmit = async () => {
    if (selected < min || selected > max) {
      toast.error(`Amount must be between ${fmtUSD(min)} and ${fmtUSD(max)}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/organizations/me/wallet/topup`,
        { amount_cents: selected },
        auth(),
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Top-up failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="org-topup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-indigo-300" /> Top up wallet
          </DialogTitle>
          <DialogDescription>
            Funds are credited instantly after Stripe confirms. No subscription —
            spend down at your own pace.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {TOPUP_PRESETS.map((cents) => (
            <button
              key={cents}
              type="button"
              onClick={() => setSelected(cents)}
              className={`rounded-md py-3 px-2 font-semibold tabular-nums transition border ${
                selected === cents
                  ? 'border-indigo-400 bg-indigo-500/15 text-indigo-100'
                  : 'border-border bg-card/60 hover:border-indigo-500/40'
              }`}
              data-testid={`topup-preset-${cents}`}
            >
              {fmtUSD(cents)}
            </button>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mt-2 space-y-1">
          <p>• {fmtUSD(selected)} → ~{Math.floor(selected * 10)} emails OR ~{Math.floor(selected / 1.5)} SMS</p>
          <p>• Paid securely via Stripe. Cards only, no recurring charge.</p>
        </div>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className="w-full mt-3 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white h-11 font-bold"
          data-testid="topup-submit-btn"
        >
          {submitting
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
            : <>Continue to Stripe <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
